/**
 * DemoHub - Main shell component with tab navigation and iframe container
 * 
 * Supports direct URLs:
 * - /sheret - Welcome screen
 * - /sheret/one - One-Hand demo
 * - /sheret/shortcuts - Shortcuts demo
 * 
 * @module demos/ecom-lab/DemoHub
 */
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEMO_HUB_ENABLED } from './index';

const DEMOS = [
  { id: 'welcome', slug: '', label: 'Welcome', icon: '🏠' },
  { id: 'one-hand', slug: 'one', label: 'One-Hand', icon: '👆' },
  { id: 'shortcuts', slug: 'shortcuts', label: 'Shortcuts', icon: '⚡' },
];

const DemoHub = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [modalDismissed, setModalDismissed] = useState({});
  
  // Parse URL to get active demo
  const getActiveDemoFromUrl = () => {
    const path = location.pathname.replace('/sheret', '').replace(/^\//, '');
    if (!path) return 'welcome';
    const demo = DEMOS.find(d => d.slug === path);
    return demo ? demo.id : 'welcome';
  };
  
  const [activeDemo, setActiveDemo] = useState(getActiveDemoFromUrl);

  // Update URL when demo changes
  const handleDemoChange = (demoId) => {
    const demo = DEMOS.find(d => d.id === demoId);
    const newPath = demo?.slug ? `/sheret/${demo.slug}` : '/sheret';
    navigate(newPath);
    setActiveDemo(demoId);
  };
  
  // Sync state with URL on location change
  useEffect(() => {
    const newDemo = getActiveDemoFromUrl();
    setActiveDemo(newDemo);
    
    // Show modal if not dismissed for this demo
    if (!modalDismissed[newDemo]) {
      setShowModal(true);
    }
  }, [location.pathname]);

  const dismissModal = () => {
    setShowModal(false);
    setModalDismissed(prev => ({ ...prev, [activeDemo]: true }));
  };

  // Kill switch check
  if (!DEMO_HUB_ENABLED) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <p className="text-slate-500">Demo Hub is currently disabled.</p>
      </div>
    );
  }

  const getDemoUrl = (demoId) => {
    const baseUrl = '/demos/ecom-lab';
    switch (demoId) {
      case 'one-hand':
        return `${baseUrl}/one-hand.html`;
      case 'shortcuts':
        return `${baseUrl}/shortcuts.html`;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-slate-100">
      {/* Header - Sheret Green Style */}
      <header className="bg-[#1a4d2e] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold">ECOM LAB</span>
          <span className="text-xs opacity-75">Innovation Demos</span>
        </div>
        <a 
          href="/" 
          className="text-sm bg-white/20 px-3 py-1 rounded hover:bg-white/30 transition-colors"
        >
          ← Exit
        </a>
      </header>

      {/* Tab Navigation */}
      <nav className="bg-white border-b border-slate-200 overflow-x-auto">
        <div className="flex">
          {DEMOS.map((demo) => (
            <button
              key={demo.id}
              onClick={() => handleDemoChange(demo.id)}
              className={`flex-shrink-0 flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeDemo === demo.id
                  ? 'border-[#1a4d2e] text-[#1a4d2e] bg-green-50'
                  : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <span className="text-lg">{demo.icon}</span>
              <span>{demo.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Demo Content Area */}
      <main className="flex-1 overflow-hidden relative">
        {activeDemo === 'welcome' ? (
          <WelcomeScreen onSelectDemo={handleDemoChange} />
        ) : (
          <iframe
            src={getDemoUrl(activeDemo)}
            className="w-full h-full border-0"
            title={`${activeDemo} Demo`}
          />
        )}
      </main>

      {/* Modal Overlay */}
      {showModal && (
        <Modal 
          demoId={activeDemo} 
          onClose={dismissModal} 
        />
      )}
    </div>
  );
};

// Modal Component
const Modal = ({ demoId, onClose }) => {
  const modalContent = {
    welcome: {
      title: "Welcome to Mike Dodgson's Ecom Lab",
      content: (
        <>
          <p className="text-slate-600 mb-4">
            Welcome! This is a demonstration site showcasing how two e-commerce innovations 
            were developed from concept to working prototype.
          </p>
          <p className="text-slate-600 mb-4">
            Think of this as a <strong>design playground</strong> — similar to how teams use Jira 
            to develop concepts, these interactive demos let you experience new ideas before 
            they're handed off to professional developers for production implementation.
          </p>
          <p className="text-slate-500 text-sm">
            Select a demo below to explore each innovation.
          </p>
        </>
      ),
    },
    'one-hand': {
      title: '👆 One-Hand Mode',
      content: (
        <>
          <p className="text-slate-600 mb-4">
            <strong>The Problem:</strong> Mobile shoppers often browse while holding something — 
            a coffee, a handrail, a child's hand. Standard e-commerce sites require two-handed operation.
          </p>
          <p className="text-slate-600 mb-4">
            <strong>The Solution:</strong> A gesture-based interface activated by a single tap. 
            A radial menu appears at your thumb position, letting you navigate, add to cart, 
            and browse using simple swipe gestures.
          </p>
          <p className="text-slate-500 text-sm">
            Try it: Enable One-Hand mode, then tap anywhere to see the radial menu.
          </p>
        </>
      ),
    },
    shortcuts: {
      title: '⚡ Shortcuts',
      content: (
        <>
          <p className="text-slate-600 mb-4">
            <strong>The Problem:</strong> Repeat buyers know exactly what they want but must 
            navigate through menus, categories, and product pages every time they order.
          </p>
          <p className="text-slate-600 mb-4">
            <strong>The Solution:</strong> Personal shortcuts that learn from your purchase history. 
            One tap to reorder favorites, quick-access to frequent categories, and predictive 
            suggestions based on your buying patterns.
          </p>
          <p className="text-slate-500 text-sm">
            Demo coming soon — this prototype is under development.
          </p>
        </>
      ),
    },
  };

  const content = modalContent[demoId] || modalContent.welcome;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-slate-900 mb-4">
          {content.title}
        </h2>
        
        <div className="mb-6">
          {content.content}
        </div>
        
        <button
          onClick={onClose}
          className="w-full bg-[#1a4d2e] text-white py-3 px-4 rounded-lg font-medium hover:bg-[#15402a] transition-colors"
        >
          Got it, let's explore
        </button>
      </div>
    </div>
  );
};

// Welcome Screen Component
const WelcomeScreen = ({ onSelectDemo }) => {
  const demoCards = [
    {
      id: 'one-hand',
      title: 'One-Hand Mode',
      desc: 'Gesture-based navigation for single-handed mobile shopping. Browse, add to cart, and checkout using only your thumb.',
      color: 'bg-emerald-600',
      icon: '👆',
      status: 'Live Demo',
    },
    {
      id: 'shortcuts',
      title: 'Shortcuts',
      desc: 'Personalized quick-access for repeat buyers. Reorder favorites and access frequent items in one tap.',
      color: 'bg-violet-600',
      icon: '⚡',
      status: 'Coming Soon',
    },
  ];

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-lg mx-auto">
        {/* Hero */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            E-Commerce Innovation Lab
          </h1>
          <p className="text-slate-600">
            Two new approaches to mobile-first product ordering.
          </p>
        </div>

        {/* Demo Cards */}
        <div className="space-y-4">
          {demoCards.map((card) => (
            <button
              key={card.id}
              onClick={() => onSelectDemo(card.id)}
              className="w-full bg-white rounded-xl shadow-sm border border-slate-200 p-5 text-left hover:shadow-lg hover:border-slate-300 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 ${card.color} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                  <span className="text-white text-2xl">{card.icon}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-900">{card.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      card.status === 'Live Demo' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                      {card.status}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">{card.desc}</p>
                </div>
                <div className="text-slate-400 group-hover:text-slate-600 group-hover:translate-x-1 transition-all">
                  →
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-10 text-center text-xs text-slate-400">
          <p>Innovation Demo Hub • Prototype Environment</p>
        </div>
      </div>
    </div>
  );
};

export default DemoHub;
